/**
 * AutomationTriggersSection — what fires this automation, as chips, plus its
 * webhook endpoints when set.
 *
 * Registered as `components.typeSpecific` for `ahoy_event_automation` with
 * `typeSpecificPosition: 'before-overview'` — triggers are the first question
 * a reader has about an automation, so they sit above the property list, and
 * the webhook endpoints (inbound trigger source, outbound delivery target)
 * sit right under them. Their association field rows are registry-hidden
 * (`hiddenAssociations`) so this is the only place they render.
 *
 * Trigger shapes, resolved per-record (see capabilities.ts):
 * - typed     → entity-type chips × event-type chips
 * - matchAll  → "Any record" + event-type chips (the harness ignores the
 *               entity-type list entirely — rendering it would be a lie)
 * - unknown   → no chips. Webhook-driven automations land here — their
 *               inbound endpoint row IS the trigger story, so the section
 *               still renders whenever an endpoint is set and self-gates
 *               away only when there are neither chips nor endpoints.
 *
 * The raw triggering properties are in `hiddenProperties`, so this is the
 * only place they render.
 */

import { useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { deriveAutomationCapabilities } from '@/lib/crm/automations/capabilities';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { getEntityTypeLabel } from '@/lib/crm/utils/entityTypeUtils';
import { WebhookIcon } from '@/lib/crm/utils/icons/CustomIcons';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { OverviewFieldRow } from '../components/OverviewFieldRow';
import type { EventTypeName } from '../timeline/types';
import { EVENT_TYPE_LABELS } from '../timeline/types';
import { CollapsibleSection } from './collapsible-section';

function toStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];
}

function eventTypeLabel(name: string): string {
  return EVENT_TYPE_LABELS[name as EventTypeName] ?? name;
}

/** The automation's inbound-trigger webhook edge (one_to_one). */
const INBOUND_WEBHOOK_ASSOCIATION = 'ahoy_automation_to_webhook';
/** The automation's outbound delivery endpoint edge (one_to_one). */
const OUTBOUND_WEBHOOK_ASSOCIATION = 'ahoy_automation_to_outbound_webhook';

function findAssociationTarget(
  object: CrmObject,
  associationTypeName: string
): string | undefined {
  return object.associations?.find(
    a => a.association_type_name === associationTypeName
  )?.target_entity_id;
}

/**
 * The automation's webhook endpoints as field rows — inbound (what feeds it)
 * and outbound (where it delivers) — rendered only when set. Shared by the
 * event-automation Triggers section and the scheduled-automation Schedule
 * section so both slideouts surface endpoints the same way, up top.
 */
export function AutomationWebhookRows({ object }: { object: CrmObject }) {
  const inboundId = findAssociationTarget(object, INBOUND_WEBHOOK_ASSOCIATION);
  const outboundId = findAssociationTarget(
    object,
    OUTBOUND_WEBHOOK_ASSOCIATION
  );
  if (!inboundId && !outboundId) {
    return null;
  }
  return (
    <div className='flex flex-col gap-1'>
      {inboundId && (
        <OverviewFieldRow icon={WebhookIcon} label='Webhook'>
          <EntityBadgeById
            entityId={inboundId}
            orgId={object.org_id}
            variant='xs'
          />
        </OverviewFieldRow>
      )}
      {outboundId && (
        <OverviewFieldRow icon={WebhookIcon} label='Outbound Webhook'>
          <EntityBadgeById
            entityId={outboundId}
            orgId={object.org_id}
            variant='xs'
          />
        </OverviewFieldRow>
      )}
    </div>
  );
}

export function AutomationTriggersSection({ object }: { object: CrmObject }) {
  const caps = useMemo(() => deriveAutomationCapabilities(object), [object]);

  const entityTypes = toStringArray(
    getPropertyValue<unknown>(object.properties?.ahoy_triggering_entity_types)
  );
  const eventTypes = toStringArray(
    getPropertyValue<unknown>(object.properties?.ahoy_triggering_event_types)
  );

  const hasEndpoints =
    !!findAssociationTarget(object, INBOUND_WEBHOOK_ASSOCIATION) ||
    !!findAssociationTarget(object, OUTBOUND_WEBHOOK_ASSOCIATION);

  // Nothing to say: no trigger chips AND no endpoints. A webhook-driven
  // automation (triggerKind 'unknown' but an inbound endpoint set) renders —
  // its endpoint row is the trigger story.
  if (caps.triggerKind === 'unknown' && !hasEndpoints) {
    return null;
  }

  return (
    // CollapsibleSection (non-collapsible) rather than a hand-rolled heading:
    // the adjacent Settings section gets its header from the same component,
    // and two headings styled by different code is how they drift apart.
    <CollapsibleSection title='Triggers' collapsible={false}>
      <div className='flex flex-col gap-2'>
        {caps.triggerKind !== 'unknown' && (
          <div className='flex flex-wrap items-center gap-1.5'>
            {caps.triggerKind === 'matchAll' ? (
              <Badge type='pill-color' color='brand' size='sm'>
                Any record
              </Badge>
            ) : (
              entityTypes.map(name => (
                <Badge key={name} type='pill-color' color='gray' size='sm'>
                  {getEntityTypeLabel(name)}
                </Badge>
              ))
            )}
            {eventTypes.length > 0 && (
              <span className='text-xs text-fg-tertiary px-0.5'>on</span>
            )}
            {eventTypes.map(name => (
              <Badge key={name} type='pill-color' color='gray' size='sm'>
                {eventTypeLabel(name)}
              </Badge>
            ))}
          </div>
        )}
        <AutomationWebhookRows object={object} />
      </div>
    </CollapsibleSection>
  );
}
